<template>
  <div class="h-full flex flex-col bg-dark-primary">
    <!-- Header -->
    <div class="flex-shrink-0 px-4 py-3 border-b border-dark">
      <div>
        <h1 class="text-lg font-semibold text-dark-primary">Compliance</h1>
        <p class="text-sm text-dark-secondary">Framework violations and defensive measures</p>
      </div>
    </div>

    <!-- Content -->
    <div class="flex-1 overflow-y-auto custom-scrollbar p-4 space-y-6">

      <!-- Loading -->
      <template v-if="loading">
        <div class="flex flex-col items-center justify-center py-12">
          <div class="w-8 h-8 border-2 border-accent border-t-transparent rounded-full animate-spin mb-4"></div>
          <p class="text-sm text-dark-secondary">Loading compliance data...</p>
        </div>
      </template>

      <!-- Empty -->
      <template v-else-if="!detections.length">
        <div class="flex flex-col items-center justify-center py-12 text-center">
          <FileX class="w-12 h-12 text-dark-secondary opacity-40 mb-4" />
          <p class="text-sm text-dark-secondary mb-1">No compliance data available</p>
          <p class="text-xs text-dark-tertiary">Compliance analysis will appear here once processing is complete</p>
        </div>
      </template>

      <template v-else>

        <!-- VIOLATIONS -->
        <div>
          <div class="flex items-center gap-2 mb-3">
            <ShieldAlert class="w-4 h-4 segura-icon" />
            <h2 class="text-sm font-semibold text-dark-primary uppercase tracking-wide">Violations</h2>
          </div>

          <div class="space-y-1">
            <div v-for="detection in detections" :key="detection.technique_id"
                 class="border border-dark rounded-lg overflow-hidden">

              <!-- Technique Header -->
              <button
                @click="toggleViolation(detection.technique_id)"
                class="w-full flex items-center justify-between px-3 py-2 bg-dark-tertiary hover:bg-dark-quaternary transition text-left"
              >
                <div class="flex items-center gap-2">
                  <ChevronRight
                    class="w-3 h-3 text-dark-secondary transition-transform"
                    :class="{ 'rotate-90': expandedViolations.has(detection.technique_id) }"
                  />
                  <span class="text-xs bg-error/10 text-error border border-error/30 rounded px-1.5 py-0.5 font-mono font-semibold">
                    {{ detection.technique_id }}
                  </span>
                  <span class="text-sm font-medium text-dark-primary">{{ detection.technique_name }}</span>
                </div>
                <span class="text-xs text-dark-secondary">
                  {{ detection.compliance?.frameworks_violated?.length ?? 0 }} frameworks
                </span>
              </button>

              <!-- Frameworks violated -->
              <div v-show="expandedViolations.has(detection.technique_id)" class="bg-dark-primary">
                <div v-for="fw in (detection.compliance?.frameworks_violated ?? [])" :key="fw.framework"
                     class="border-t border-dark">

                  <!-- Framework sub-header -->
                  <button
                    @click="toggleFramework(`v-${detection.technique_id}-${fw.framework}`)"
                    class="w-full flex items-center justify-between px-5 py-2 hover:bg-dark-tertiary/50 transition text-left"
                  >
                    <div class="flex items-center gap-2">
                      <ChevronRight
                        class="w-3 h-3 text-dark-secondary transition-transform"
                        :class="{ 'rotate-90': expandedFrameworks.has(`v-${detection.technique_id}-${fw.framework}`) }"
                      />
                      <span
                        class="px-2 py-0.5 text-xs rounded font-medium"
                        :class="getFrameworkStyle(fw.framework)"
                      >
                        {{ getFrameworkShortName(fw.framework) }}
                      </span>
                      <span class="text-xs text-dark-secondary">{{ fw.framework }}</span>
                    </div>
                    <span class="text-xs text-dark-tertiary">{{ fw.controls?.length ?? 0 }} controls</span>
                  </button>

                  <!-- Controls -->
                  <div v-show="expandedFrameworks.has(`v-${detection.technique_id}-${fw.framework}`)"
                       class="px-8 pb-3 space-y-1">
                    <div v-for="control in fw.controls" :key="control"
                         class="flex items-start gap-2 py-1">
                      <div class="w-1 h-1 rounded-full bg-error mt-2 flex-shrink-0"></div>
                      <p class="text-xs text-dark-primary leading-relaxed">{{ control }}</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- DEFENSIVE MEASURES -->
        <div>
          <div class="flex items-center gap-2 mb-3">
            <ShieldCheck class="w-4 h-4 segura-icon" />
            <h2 class="text-sm font-semibold text-dark-primary uppercase tracking-wide">Measures</h2>
          </div>

          <div class="space-y-1">
            <div v-for="(controls, framework) in groupedDefensiveMeasures" :key="framework"
                 class="border border-dark rounded-lg overflow-hidden">

              <!-- Framework Header -->
              <button
                @click="toggleDefensive(framework)"
                class="w-full flex items-center justify-between px-3 py-2 bg-dark-tertiary hover:bg-dark-quaternary transition text-left"
              >
                <div class="flex items-center gap-2">
                  <ChevronRight
                    class="w-3 h-3 text-dark-secondary transition-transform"
                    :class="{ 'rotate-90': expandedDefensive.has(framework) }"
                  />
                  <span
                    class="px-2 py-0.5 text-xs rounded font-medium"
                    :class="getFrameworkStyle(framework)"
                  >
                    {{ getFrameworkShortName(framework) }}
                  </span>
                  <span class="text-sm font-medium text-dark-primary">{{ framework }}</span>
                </div>
                <span class="text-xs text-dark-secondary">{{ controls.length }} controls</span>
              </button>

              <!-- Controls -->
              <div v-show="expandedDefensive.has(framework)" class="bg-dark-primary">
                <div v-for="control in controls" :key="control"
                     class="flex items-start gap-2 px-5 py-2 border-t border-dark">
                  <div class="w-1 h-1 rounded-full bg-green-500 mt-2 flex-shrink-0"></div>
                  <p class="text-xs text-dark-primary leading-relaxed">{{ control }}</p>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- RECOMMENDATIONS -->
        <div v-if="recommendations.length">
          <div class="flex items-center gap-2 mb-3">
            <Lightbulb class="w-4 h-4 segura-icon" />
            <h2 class="text-sm font-semibold text-dark-primary uppercase tracking-wide">Recommendations</h2>
          </div>

          <div class="space-y-2">
            <div v-for="(rec, idx) in recommendations" :key="idx"
                 class="flex items-start gap-3 bg-dark-tertiary border border-dark rounded-lg p-3">
              <span class="text-xs font-mono font-bold text-accent mt-0.5 flex-shrink-0">
                {{ String(idx + 1).padStart(2, '0') }}
              </span>
              <p class="text-xs text-dark-primary leading-relaxed">{{ rec }}</p>
            </div>
          </div>
        </div>

      </template>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted, watch } from "vue";
import { ChevronRight, FileX, ShieldAlert, ShieldCheck, Lightbulb } from "lucide-vue-next";
import { useSessionStore } from "../stores/sessionStore";
import { apiClient } from "../services/apiClient";

const props = defineProps({
  session: { type: Object, default: null },
});

const sessionStore = useSessionStore();

// State
const loading          = ref(true);
const detections       = ref([]);
const recommendations  = ref([]);
const expandedViolations  = ref(new Set());
const expandedFrameworks  = ref(new Set());
const expandedDefensive   = ref(new Set());

// Current session
const currentSession = computed(() => props.session || sessionStore.currentSession);

// ---------------------------------------------------------------------------
// Defensive measures grouped by framework (deduplicated)
// ---------------------------------------------------------------------------
const groupedDefensiveMeasures = computed(() => {
  const groups = {};

  detections.value.forEach(detection => {
    (detection.compliance?.defensive_measures ?? []).forEach(fw => {
      if (!groups[fw.framework]) groups[fw.framework] = new Set();
      (fw.controls ?? []).forEach(c => groups[fw.framework].add(c));
    });
  });

  return Object.fromEntries(
    Object.entries(groups).map(([fw, controls]) => [fw, [...controls]])
  );
});

// ---------------------------------------------------------------------------
// Data loading
// ---------------------------------------------------------------------------
async function loadComplianceData() {
  const sessionId = currentSession.value?.id;
  if (!sessionId) return;

  loading.value = true;
  detections.value = [];
  recommendations.value = [];

  try {
    const result = await apiClient(`/summaries?session_id=${sessionId}&compliance=true`);
    if (!result) return;

    detections.value = result.detections ?? [];
    recommendations.value = result.recommendations ?? [];

    // Debug: verificar estrutura dos dados
    console.log('[CompliancePanel] First detection structure:', JSON.stringify(detections.value[0], null, 2));
    console.log(`[CompliancePanel] ${detections.value.length} detections loaded`);

    // Auto-expand first violation and first defensive framework
    if (detections.value.length) {
      expandedViolations.value.add(detections.value[0].technique_id);
      expandedViolations.value = new Set(expandedViolations.value);
    }
    const firstDefensive = Object.keys(groupedDefensiveMeasures.value)[0];
    if (firstDefensive) {
      expandedDefensive.value.add(firstDefensive);
      expandedDefensive.value = new Set(expandedDefensive.value);
    }

  } catch (err) {
    console.error("[CompliancePanel] Failed to load:", err);
  } finally {
    loading.value = false;
  }
}

// ---------------------------------------------------------------------------
// Toggle helpers
// ---------------------------------------------------------------------------
function toggle(set, key) {
  const next = new Set(set.value);
  next.has(key) ? next.delete(key) : next.add(key);
  set.value = next;
}

function toggleViolation(id)       { toggle(expandedViolations, id); }
function toggleFramework(id)       { toggle(expandedFrameworks, id); }
function toggleDefensive(framework){ toggle(expandedDefensive, framework); }

// ---------------------------------------------------------------------------
// Framework styling
// ---------------------------------------------------------------------------
function getFrameworkStyle(framework) {
  const f = framework.toLowerCase();
  if (f.includes('iso'))        return 'bg-purple-600 text-white';
  if (f.includes('nist') && f.includes('csf')) return 'bg-sky-600 text-white';
  if (f.includes('nist'))       return 'bg-blue-700 text-white';
  if (f.includes('cis'))        return 'bg-orange-600 text-white';
  if (f.includes('soc'))        return 'bg-amber-500 text-white';
  if (f.includes('isa') || f.includes('iec 62443')) return 'bg-indigo-600 text-white';
  if (f.includes('d3fend'))     return 'bg-green-600 text-white';
  if (f.includes('gdpr'))       return 'bg-teal-600 text-white';
  if (f.includes('pci'))        return 'bg-orange-700 text-white';
  if (f.includes('hipaa'))      return 'bg-blue-600 text-white';
  return 'bg-slate-600 text-white';
}

function getFrameworkShortName(framework) {
  const map = {
    'ISO/IEC 27001:2022':                     '27001',
    'NIST SP 800-53 Rev. 5':                  'NIST',
    'NIST Cybersecurity Framework (CSF) 2.0': 'CSF',
    'CIS Controls v8':                        'CIS',
    'SOC 2 (Trust Service Criteria)':         'SOC 2',
    'ISA/IEC 62443':                          '62443',
    'MITRE D3FEND':                           'D3FEND',
    'GDPR':                                   'GDPR',
    'PCI-DSS':                                'PCI',
    'HIPAA':                                  'HIPAA',
  };
  return map[framework] || framework.split(' ').slice(0, 2).join(' ');
}

// ---------------------------------------------------------------------------
// Lifecycle
// ---------------------------------------------------------------------------
watch(() => currentSession.value?.id, async (newId) => {
  if (newId) await loadComplianceData();
});

onMounted(() => loadComplianceData());
</script>

<style scoped>
.segura-icon {
  color: #006b69;
}
html[data-theme="dark"] .segura-icon,
.dark .segura-icon {
  color: #00d1b5;
}
.custom-scrollbar {
  scrollbar-width: thin;
  scrollbar-color: rgba(255, 255, 255, 0.1) transparent;
}
.custom-scrollbar::-webkit-scrollbar { width: 6px; }
.custom-scrollbar::-webkit-scrollbar-track { background: transparent; }
.custom-scrollbar::-webkit-scrollbar-thumb { background: rgba(255, 255, 255, 0.1); border-radius: 3px; }
.animate-spin { animation: spin 1s linear infinite; }
@keyframes spin { to { transform: rotate(360deg); } }
</style>